/**
 * What a comanda card decides, separated from how it looks.
 *
 * These rules used to live inline in a 912-line component, which meant none of
 * them could be checked without a browser and a service in progress. Two of
 * them carry real weight:
 *
 * - The active wave drives the timer and the pulsing card. When every called
 *   wave is finished the card must go quiet, otherwise the cook learns that the
 *   pulsing means nothing.
 * - The row order must never depend on status. The cook presses these rows; a
 *   row that re-sorts the moment it is pressed puts the next press on the wrong
 *   dish.
 */

import { groupKdsItems, groupedLineStatus } from "@shared/kds/grouping";
import type { KdsT } from "./i18n/settings";

/**
 * The chef's last word is READY. SERVED belongs to the waiter, who says it when
 * the plate actually reaches the table.
 */
export const STATUS_CYCLE: Record<string, string> = {
  PENDING: "PREPARING",
  PREPARING: "READY",
};

export const STATUS_DOT_CLASS: Record<string, string> = {
  PENDING: "bg-kds-pending",
  CONFIRMED: "bg-kds-pending",
  IN_PROGRESS: "bg-kds-preparing",
  PREPARING: "bg-kds-preparing",
  READY: "bg-kds-ready",
  SERVED: "bg-kds-served",
  COMPLETED: "bg-kds-served",
  CANCELLED: "bg-kds-cancelled",
};

/** What the cook reads for each status: `STATUS_LABEL[status]?.(t)`. */
export const STATUS_LABEL: Record<string, (t: KdsT) => string> = {
  PENDING: (t) => t("kds.status.toDo", "Da fare"),
  CONFIRMED: (t) => t("kds.status.toDo", "Da fare"),
  IN_PROGRESS: (t) => t("kds.status.inPrep", "In prep."),
  PREPARING: (t) => t("kds.status.inPrep", "In prep."),
  READY: (t) => t("kds.status.ready", "Pronto"),
  SERVED: (t) => t("kds.status.served", "Servito"),
  COMPLETED: (t) => t("kds.status.completed", "Completato"),
  CANCELLED: (t) => t("kds.status.cancelled", "Annullato"),
};

export const CARD_BORDER_CLASS: Record<string, string> = {
  PENDING: "border-kds-pending",
  CONFIRMED: "border-kds-pending",
  IN_PROGRESS: "border-kds-preparing",
  READY: "border-kds-ready",
  SERVED: "border-kds-served",
  COMPLETED: "border-kds-served",
  CANCELLED: "border-kds-cancelled",
};

/** Statuses that still need the kitchen's hands. */
const IN_PROGRESS_STATUSES = new Set([
  "PENDING",
  "CONFIRMED",
  "PREPARING",
  // i18n-keep: order item status code compared with the payload, never shown
  "IN_PROGRESS",
]);

/** Which wave a dish belongs to, whatever the payload calls it. */
export function waveOf(item: any): number {
  return item?.serviceWave ?? item?.courseNumber ?? 1;
}

/**
 * The dishes this card should show.
 *
 * Cancelled rows are kept off the cook's screen entirely: they are distracting
 * and they cause the expensive mistake, which is cooking something nobody is
 * going to pay for.
 */
export function visibleItemsFor(order: any, stationId: string | undefined): any[] {
  const all: any[] = Array.isArray(order?.items) ? order.items : [];
  const scoped = stationId ? all.filter((i) => i?.stationId === stationId) : all;
  return scoped.filter((i) => i?.status !== "CANCELLED");
}

/**
 * When each wave was called into the kitchen — the earliest call wins, because
 * that is when the cooking actually started.
 */
export function waveFiredAtMap(items: any[]): Map<number, Date | null> {
  const map = new Map<number, Date | null>();
  for (const i of items) {
    const wave = waveOf(i);
    const fired = i?.firedAt ? new Date(i.firedAt) : null;
    const existing = map.get(wave);
    if (fired && (!existing || fired < existing)) map.set(wave, fired);
    else if (existing === undefined) map.set(wave, fired);
  }
  return map;
}

/**
 * The wave that is cooking right now: the most recent called wave that still
 * has a dish in the kitchen's hands. `null` when the kitchen is done — and the
 * card must then stop ticking and stop pulsing.
 */
export function activeWaveOf(
  waveFiredAt: Map<number, Date | null>,
  items: any[],
): number | null {
  const called = [...waveFiredAt.entries()]
    .filter(([, firedAt]) => firedAt !== null)
    .map(([wave]) => wave)
    .sort((a, b) => b - a);

  for (const wave of called) {
    const stillCooking = items.some(
      (i) =>
        waveOf(i) === wave &&
        i?.status !== "READY" &&
        i?.status !== "SERVED" &&
        i?.status !== "CANCELLED",
    );
    if (stillCooking) return wave;
  }
  return null;
}

/**
 * How many plates a row stands for. A row the waiter sent as "three of these"
 * is three plates to make, exactly like three separate rows would be.
 */
export function portionsOf(item: any): number {
  return Math.max(1, Math.floor(Number(item?.quantity)) || 1);
}

/**
 * The order the rows are drawn in, and it never depends on status: the cook
 * presses these rows, and a row that re-sorts the instant it is pressed puts
 * the next press on the wrong dish.
 */
function compareRows(a: any, b: any): number {
  const waveDiff = waveOf(a) - waveOf(b);
  if (waveDiff !== 0) return waveDiff;

  const createdA = new Date(a?.createdAt ?? 0).getTime();
  const createdB = new Date(b?.createdAt ?? 0).getTime();
  if (createdA !== createdB) return createdA - createdB;

  return String(a?._virtualKey ?? a?.id).localeCompare(String(b?._virtualKey ?? b?.id));
}

/**
 * One row per portion, in an order that never moves.
 *
 * A dish ordered three times is three plates to make, so it gets three rows.
 * The action still targets the single row in the database, so marking one ready
 * marks them all — which is correct: the kitchen finishes them together.
 *
 * The sort uses wave and then immutable keys. Never status: a refetch after a
 * status change would otherwise re-order the rows under the cook's finger.
 */
export function expandAndSortItems(items: any[]): any[] {
  const expanded = items.flatMap((item) => {
    const portions = portionsOf(item);
    return Array.from({ length: portions }, (_, index) => ({
      ...item,
      _virtualKey: portions > 1 ? `${item.id}-${index}` : item.id,
    }));
  });

  return expanded.sort(compareRows);
}

/** One line of a station card, whether or not it stands for several plates. */
export interface StationLine {
  /** Stable across refetches, so React keeps the row under the finger. */
  key: string;
  /** What the cook reads: the first portion, wearing the line's status. */
  item: any;
  /**
   * The ORDER ITEMS behind the line — database rows, never plates. Actions hang
   * off these: a line of three sent as one row is one request, not three.
   */
  portions: any[];
  /** How many plates to make. */
  count: number;
  /** How many plates sit at each status. */
  counts: Record<string, number>;
}

/**
 * The lines a station card draws.
 *
 * With grouping off this is `expandAndSortItems` exactly as it has always been:
 * one row per plate, one plate per line, nothing to open. That is what keeps a
 * venue that never touched the switch indistinguishable from today.
 *
 * With it on, plates identical in every respect share one line carrying their
 * count — the rule the table board has followed since the switch was built,
 * finally applied to the view the chef's screen actually opens on.
 *
 * The count sums quantities instead of counting rows, so one row ordered three
 * times and three rows ordered once both read "×3". A collapsed line wears the
 * LEAST advanced status of its plates, so it can never claim to be further
 * along than its slowest one.
 */
export function stationLines(items: any[], grouping: boolean): StationLine[] {
  if (!grouping) {
    return expandAndSortItems(items).map((item) => ({
      key: String(item._virtualKey ?? item.id),
      item,
      portions: [item],
      count: 1,
      counts: { [item?.status ?? "PENDING"]: 1 },
    }));
  }

  const sorted = [...items].sort(compareRows);

  return groupKdsItems(sorted, true).map((line) => {
    // One entry per PLATE, so a row sent as "three of these" weighs three in
    // both the count and the "2 di 3 pronti" hint.
    const statuses = line.portions.flatMap((portion: any) =>
      Array.from({ length: portionsOf(portion) }, () => portion?.status ?? "PENDING"),
    );
    const { status, counts } = groupedLineStatus(statuses);
    const [first] = line.portions;

    return {
      key: String(first?.id),
      item: { ...first, status },
      portions: line.portions,
      count: statuses.length,
      counts,
    };
  });
}

export interface OrderCardFlags {
  /** There is a next wave with dishes nobody has called yet. */
  hasUnfiredWave: boolean;
  /** Every dish is ready or already out. */
  allItemsDone: boolean;
  /** At least one called dish is still the kitchen's to finish. */
  hasCookingFired: boolean;
}

/**
 * The three questions the card's buttons hang on.
 *
 * `hasCookingFired` is the one worth reading twice: a wave the waiter has not
 * sent in is not the cook's to finish, so the "all ready" shortcut must not
 * offer to finish it.
 */
export function orderCardFlags({
  order,
  items,
}: {
  order: any;
  items: any[];
}): OrderCardFlags {
  const currentWave = order?.currentWave ?? order?.currentCourse ?? 1;
  const nextWave = currentWave + 1;

  const hasUnfiredWave = items.some(
    (i) => waveOf(i) === nextWave && !i?.firedAt && i?.status !== "CANCELLED",
  );

  const allItemsDone =
    items.length > 0 &&
    items.every((i) => i?.status === "READY" || i?.status === "SERVED");

  const hasCookingFired = items.some(
    (i) => !!i?.firedAt && IN_PROGRESS_STATUSES.has(i?.status),
  );

  return { hasUnfiredWave, allItemsDone, hasCookingFired };
}
